import { PropsWithChildren, Suspense } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import * as Dialog from '@givebutter/primitives/dialog';
import * as Skeleton from '@givebutter/primitives/skeleton';
import { useToast } from '@givebutter/primitives/toast';
import { faSync } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { READY_TO_SYNC_FILTER } from './constants';
import { QuickBooksDetailsResponse, QuickBooksProcessMode, manualSync } from 'api/quickBooks';
import { fetchTransactions } from 'api/transactions';
import {
    useQuickBooksDetails,
    useQuickBooksStore,
} from 'assets/js/pages/account-settings/account-integrations/QuickBooks/useQuickBooksDetails';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { transactionQueryKeys } from 'queries/transaction-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface ManualSyncDialogProps extends Dialog.DialogProps {}

const ManualSyncDialog = ({ children, ...props }: ManualSyncDialogProps) => {
    return (
        <Dialog.Root {...props}>
            {children}
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content>
                    <Dialog.Close />
                    <Dialog.Body>
                        <ManualSyncDialogContent />
                    </Dialog.Body>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
};

const ManualSyncDialogHeader = () => {
    return (
        <Dialog.Header>
            <div
                className={
                    'flex h-12 w-12 flex-col items-center justify-center rounded-full bg-blue-100 text-center'
                }
                aria-label={'Sync now'}
            >
                <FontAwesomeIcon
                    className={'flex h-6 w-6 text-blue-600'}
                    icon={faSync}
                    aria-hidden="true"
                />
            </div>
        </Dialog.Header>
    );
};

const ManualSyncDialogAnimation = () => {
    const { data } = useQuickBooksDetails();
    const isSyncing = data.process_mode !== QuickBooksProcessMode.Idle;
    if (!isSyncing) {
        return null;
    }

    return (
        <video
            className="-mt-4"
            src={'/animations/quickbooks-sync.mp4'}
            autoPlay
            controls={false}
            loop
            muted
        />
    );
};

const ManualSyncDialogContent = () => {
    return (
        <>
            <ManualSyncDialogHeader />
            <Suspense fallback={<Skeleton.BigText />}>
                <ManualSyncDialogAnimation />
                <ManualSyncDialogTitle />
                <ManualSyncDialogDescription />
                <ManualSyncDialogActions />
            </Suspense>
        </>
    );
};

const ManualSyncDialogTitle = () => {
    return (
        <Dialog.Title>
            <ManualSyncInProgress>Sync in Progress</ManualSyncInProgress>
            <ManualSyncReady>Run a manual sync?</ManualSyncReady>
            <ManualSyncEmpty>Nothing to sync right now</ManualSyncEmpty>
        </Dialog.Title>
    );
};

const ManualSyncDialogDescription = () => {
    return (
        <Dialog.Description>
            <ManualSyncInProgress>
                This usually takes just a minute or two. Feel free to close this dialog. Your
                transactions are syncing in the background.
            </ManualSyncInProgress>
            <ManualSyncReady>
                This usually takes just a minute or two. Once started, all ready to sync
                transactions will be processed using your current mappings.
            </ManualSyncReady>
            <ManualSyncEmpty>No transactions are ready to sync at this time.</ManualSyncEmpty>
        </Dialog.Description>
    );
};

const ManualSyncDialogActions = () => {
    const { accountId } = useCurrentAccount();
    const { data } = useQuickBooksDetails();
    const setForcedSync = useQuickBooksStore(state => state.setForcedSync);
    const { error, success } = useToast();
    const queryClient = useQueryClient();

    const { data: count } = useReadyToSyncTransactionsCount();

    const { isPending, mutate: runManualSync } = useMutation({
        mutationFn: () => {
            return manualSync(accountId);
        },
        onSuccess: () => {
            success('QuickBooks sync started!');
            setForcedSync(true);
            queryClient.setQueryData<QuickBooksDetailsResponse>(
                accountQueryKeys.quickBooksDetails(accountId),
                data => {
                    if (!data) return data;

                    return {
                        ...data,
                        process_mode: QuickBooksProcessMode.Queued,
                    };
                },
            );
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Unknown error');
        },
    });

    const isSyncing = data.process_mode !== QuickBooksProcessMode.Idle;

    return (
        <div className="flex w-full gap-2">
            {count > 0 && !isSyncing ? (
                <>
                    <Dialog.Cancel asChild>
                        <Button variant="outline" color="neutral" className="flex-grow">
                            Cancel
                        </Button>
                    </Dialog.Cancel>
                    <Button
                        variant="solid"
                        onClick={() => runManualSync()}
                        className="flex-grow"
                        disabled={isPending}
                    >
                        Run sync
                    </Button>
                </>
            ) : (
                <Dialog.Cancel asChild>
                    <Button variant="outline" color="neutral" className="flex-grow">
                        Close
                    </Button>
                </Dialog.Cancel>
            )}
        </div>
    );
};

const ManualSyncInProgress = ({ children }: PropsWithChildren) => {
    const { data } = useQuickBooksDetails();
    const isSyncing = data.process_mode !== QuickBooksProcessMode.Idle;

    if (!isSyncing) {
        return null;
    }

    return <>{children}</>;
};

const ManualSyncEmpty = ({ children }: PropsWithChildren) => {
    const { data: count } = useReadyToSyncTransactionsCount();
    const { data } = useQuickBooksDetails();
    const isSyncing = data.process_mode !== QuickBooksProcessMode.Idle;

    if (isSyncing || count > 0) {
        return null;
    }

    return <>{children}</>;
};

const ManualSyncReady = ({ children }: PropsWithChildren) => {
    const { data: count } = useReadyToSyncTransactionsCount();
    const { data } = useQuickBooksDetails();
    const isSyncing = data.process_mode !== QuickBooksProcessMode.Idle;

    if (isSyncing || count === 0) {
        return null;
    }

    return <>{children}</>;
};

const useReadyToSyncTransactionsCount = () => {
    const { accountId } = useCurrentAccount();

    return useSuspenseQuery({
        queryKey: transactionQueryKeys.transactions(accountId),
        queryFn: () =>
            fetchTransactions(accountId, {
                filters: READY_TO_SYNC_FILTER,
                page: 1,
                per_page: 1,
            }),
        select: data => data.meta.total,
        refetchOnMount: 'always',
    });
};

const Root = ManualSyncDialog;
const ManualSyncDialogTrigger = Dialog.Trigger;
const Trigger = ManualSyncDialogTrigger;

export {
    Root,
    Trigger,
    //
    ManualSyncDialog,
    ManualSyncDialogTrigger,
};
